import { z } from 'zod';
import { bilingueSchema, dateSaisieSchema, idSchema } from './model/commun.js';
export const TYPES_EVENEMENT = [
    'vernissage',
    'visite',
    'conference',
    'signature',
    'finissage',
    'autre',
];
export const ETATS_EVENEMENT = ['brouillon', 'publie', 'annule'];
export const ETATS_INSCRIPTION = ['confirmee', 'liste_attente', 'annulee', 'en_paiement'];
export const RAPPELS_EVENEMENT = ['aucun', '24h', '48h'];
export const ACCES_EVENEMENT = ['public', 'sur_lien'];
const texte = bilingueSchema.extend({
    fr: z.string().trim().max(10_000),
    en: z.string().trim().max(10_000).optional(),
});
export const tarifEvenementSchema = z.strictObject({
    id: z
        .string()
        .regex(/^[A-Za-z0-9_-]{1,40}$/)
        .optional(),
    libelle: texte,
    prixCentimes: z.int().min(0),
});
const champs = z.strictObject({
    type: z.enum(TYPES_EVENEMENT),
    titre: texte.optional(),
    description: texte.optional(),
    messageConfirmation: texte.optional(),
    expositionId: idSchema.nullable().optional(),
    debut: dateSaisieSchema.nullable().optional(),
    fin: dateSaisieSchema.nullable().optional(),
    lieuId: idSchema.nullable().optional(),
    adresseLibre: z.string().trim().max(1000).optional(),
    acces: z.enum(ACCES_EVENEMENT).optional(),
    jauge: z.int().min(1).nullable().optional(),
    placesParInscriptionMax: z.int().min(1).max(10).optional(),
    listeAttente: z.boolean().optional(),
    inscriptionsDesLe: dateSaisieSchema.nullable().optional(),
    inscriptionsJusquAu: dateSaisieSchema.nullable().optional(),
    tarifs: z
        .array(tarifEvenementSchema)
        .min(1)
        .max(5)
        .refine((tarifs) => new Set(tarifs.flatMap((t) => (t.id ? [t.id] : []))).size ===
        tarifs.filter((t) => t.id).length, 'Les identifiants de tarif doivent être uniques.')
        .optional(),
    rappel: z.enum(RAPPELS_EVENEMENT).optional(),
});
export const creationEvenementSchema = champs;
export const modificationEvenementSchema = champs.partial().extend({ version: z.int().min(1) });
export const inscriptionRegieSchema = z
    .strictObject({
    contactId: idSchema.optional(),
    nom: z.string().trim().min(1).max(200).optional(),
    email: z.email().max(320).optional(),
    telephone: z.string().trim().max(100).optional(),
    langue: z.enum(['fr', 'en']).optional(),
    places: z.int().min(1).max(10).default(1),
    tarifId: z.string().max(40).optional(),
    forcer: z.boolean().default(false),
})
    .refine((d) => !!d.contactId || (!!d.nom && !!d.email), 'Indiquez un contact ou un nom et un e-mail.');
export const inscriptionPubliqueSchema = z.strictObject({
    nom: z.string().trim().min(1).max(200),
    email: z.email().max(320),
    telephone: z.string().trim().max(100).optional(),
    langue: z.enum(['fr', 'en']).default('fr'),
    places: z.int().min(1).max(10),
    tarifId: z.string().max(40).optional(),
    confidentialite: z.literal(true),
    newsletter: z.boolean().default(false),
    site_web: z.string().max(200).default(''),
});
export const messageEvenementSchema = z.strictObject({
    sujet: z.string().trim().min(1).max(200),
    corps: z.string().trim().min(1).max(5000),
    cible: z.enum(['confirmes', 'confirmes_et_attente']),
});
export function datesEffectives(evenement, vernissage) {
    if (evenement.type === 'vernissage')
        return vernissage ?? { debut: null, fin: null };
    return { debut: evenement.debut ?? null, fin: evenement.fin ?? null };
}
export function inscriptionsOuvertes(evenement, dates, maintenant) {
    if (evenement.etat !== 'publie' || !dates.debut)
        return false;
    const fin = evenement.inscriptionsJusquAu ?? dates.debut;
    return ((!evenement.inscriptionsDesLe || maintenant >= evenement.inscriptionsDesLe) &&
        maintenant < fin &&
        maintenant < (dates.fin ?? new Date(+dates.debut + 4 * 3_600_000)));
}
export function phaseEvenement(evenement, dates, maintenant) {
    if (evenement.etat === 'annule')
        return 'annule';
    if (evenement.etat === 'brouillon')
        return 'brouillon';
    if (!dates.debut)
        return 'sans_date';
    if (maintenant >= (dates.fin ?? new Date(+dates.debut + 4 * 3_600_000)))
        return 'clos';
    if (maintenant >= dates.debut)
        return 'en_cours';
    if (inscriptionsOuvertes(evenement, dates, maintenant))
        return 'inscriptions_ouvertes';
    return 'a_venir';
}
export function emargementOuvert(evenement, dates, maintenant) {
    if (evenement.etat !== 'publie' || !dates.debut)
        return false;
    const limite = dates.fin ? +dates.fin + 2 * 3_600_000 : +dates.debut + 12 * 3_600_000;
    // Les portes ouvrent souvent avant l’heure annoncée : deux heures de marge.
    return +maintenant >= +dates.debut - 2 * 3_600_000 && +maintenant < limite;
}
export function placesRestantes(evenement) {
    return evenement.jauge === null
        ? null
        : Math.max(0, evenement.jauge - evenement.placesConfirmees);
}
//# sourceMappingURL=evenements.js.map